import { useState, useMemo } from 'react'
import { AlertTriangle, Shield, Zap, TrendingUp, Clock, Users, BarChart3, Eye, CheckCircle2, Search, Filter, Download, Settings, Terminal, Activity, Wifi, Lock, Gauge, Target, TrendingDown, Database, Cpu, Server, Plus, ChevronDown, Power, Upload, RefreshCw, Play, Pause, Send, Trash2, Edit2, X } from 'lucide-react'

export default function CyberStrikePage() {
  // Advanced Findings and Alerts Data
  const [findings, setFindings] = useState([
    { id: 'FIN-2025-001', title: '24 hour risk threshold exceeded for system=cybersecurity-prod-1', severity: 'Critical', priority: 1790, status: 'New', timestamp: 'Sep 25, 2025 5:15 PM', assignee: 'unassigned', type: 'Investigation', integrityScore: 100, intermediateCount: 3, eventCount: 287, logSource: 'firewall_logs', threatLevel: 'Critical' },
    { id: 'FIN-2025-002', title: 'Excessive Failed Logins - Possible Brute Force Attack', severity: 'Critical', priority: 1250, status: 'New', timestamp: 'Sep 25, 2025 3:30 PM', assignee: 'unassigned', type: 'Finding', integrityScore: 95, intermediateCount: 5, eventCount: 423, logSource: 'auth_logs', threatLevel: 'Critical' },
    { id: 'FIN-2025-003', title: 'Unusual Volume of Network Activity Detected - Port Scanning', severity: 'High', priority: 950, status: 'In Review', timestamp: 'Sep 25, 2025 2:15 PM', assignee: 'Sean Cain', type: 'Finding', integrityScore: 88, intermediateCount: 2, eventCount: 156, logSource: 'network_ids', threatLevel: 'High' },
    { id: 'FIN-2025-004', title: 'Suspicious PowerShell Execution - Potential Lateral Movement', severity: 'Critical', priority: 1640, status: 'New', timestamp: 'Sep 24, 2025 11:45 PM', assignee: 'unassigned', type: 'Investigation', integrityScore: 100, intermediateCount: 8, eventCount: 512, logSource: 'endpoint_logs', threatLevel: 'Critical' },
    { id: 'FIN-2025-005', title: 'Data Exfiltration Attempt Detected - 2.3GB Transfer', severity: 'Critical', priority: 1890, status: 'New', timestamp: 'Sep 24, 2025 8:30 PM', assignee: 'unassigned', type: 'Investigation', integrityScore: 100, intermediateCount: 6, eventCount: 634, logSource: 'dlp_logs', threatLevel: 'Critical' },
  ])

  const [investigations, setInvestigations] = useState([
    { id: 'INV-2025-001', title: 'Risk Threshold Exceeded - cybersecurity-prod-1', status: 'Active', owner: 'unassigned', findings: 3, detections: 12, severity: 'Critical', affectedSystems: 24, progress: 35 },
    { id: 'INV-2025-002', title: 'Possible Data Exfiltration - Finance Department', status: 'Active', owner: 'Sean Cain', findings: 2, detections: 8, severity: 'Critical', affectedSystems: 15, progress: 65 },
    { id: 'INV-2025-003', title: 'Credential Harvesting Campaign - Phishing', status: 'In Progress', owner: 'Admin', findings: 5, detections: 15, severity: 'High', affectedSystems: 42, progress: 80 },
  ])

  const [dataSources, setDataSources] = useState([
    { id: 'DS-001', name: 'Firewall Logs', status: 'Connected', type: 'Network', events: '45,821/24h', lastUpdate: '2 mins ago', icon: '🔥' },
    { id: 'DS-002', name: 'Endpoint Security', status: 'Connected', type: 'EDR', events: '28,934/24h', lastUpdate: '1 min ago', icon: '🖥️' },
    { id: 'DS-003', name: 'Active Directory', status: 'Connected', type: 'IAM', events: '18,294/24h', lastUpdate: '3 mins ago', icon: '🔑' },
    { id: 'DS-004', name: 'Web Application Firewall', status: 'Connected', type: 'WAF', events: '12,456/24h', lastUpdate: '5 mins ago', icon: '🌐' },
    { id: 'DS-005', name: 'Cloud Security', status: 'Disconnected', type: 'Cloud', events: '0/24h', lastUpdate: 'Never', icon: '☁️' },
  ])

  const [selectedResponse, setSelectedResponse] = useState(null)
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterSeverity, setFilterSeverity] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('dashboard')
  const [advancedMode, setAdvancedMode] = useState(false)

  const filteredFindings = useMemo(() => {
    return findings.filter(f => {
      const matchStatus = filterStatus === 'all' || f.status === filterStatus
      const matchSeverity = filterSeverity === 'all' || f.severity === filterSeverity
      const matchSearch = searchQuery === '' ||
        f.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.id.toLowerCase().includes(searchQuery.toLowerCase())
      return matchStatus && matchSeverity && matchSearch
    }).sort((a, b) => b.priority - a.priority)
  }, [findings, filterStatus, filterSeverity, searchQuery])

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'Critical': return 'bg-red-500/20 text-red-300 border-red-500/50'
      case 'High': return 'bg-orange-500/20 text-orange-300 border-orange-500/50'
      case 'Medium': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50'
      default: return 'bg-blue-500/20 text-blue-300 border-blue-500/50'
    }
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'New': return 'bg-red-500/20 text-red-300'
      case 'In Review': return 'bg-yellow-500/20 text-yellow-300'
      case 'In Progress': return 'bg-cyan-500/20 text-cyan-300'
      case 'Resolved': return 'bg-green-500/20 text-green-300'
      case 'Active': return 'bg-red-500/20 text-red-300'
      case 'Connected': return 'bg-green-500/20 text-green-300'
      case 'Disconnected': return 'bg-red-500/20 text-red-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  const criticalCount = findings.filter(f => f.severity === 'Critical').length
  const highCount = findings.filter(f => f.severity === 'High').length
  const totalEvents = findings.reduce((sum, f) => sum + f.eventCount, 0)
  const connectedSources = dataSources.filter(d => d.status === 'Connected').length

  const stats = [
    { label: 'Critical Findings', value: criticalCount, icon: AlertTriangle, color: 'from-red-600 to-red-500', trend: '+2 today' },
    { label: 'High Priority', value: highCount, icon: TrendingUp, color: 'from-orange-600 to-orange-500', trend: '+1 today' },
    { label: 'Events (24h)', value: totalEvents.toLocaleString(), icon: Activity, color: 'from-cyan-600 to-cyan-500', trend: '↑ 12%' },
    { label: 'Data Sources', value: `${connectedSources}/${dataSources.length}`, icon: Database, color: 'from-purple-600 to-purple-500', trend: 'Connected' },
  ]

  const responseActions = [
    { id: 1, label: 'Start Investigation', icon: Eye, color: 'blue', desc: 'Create formal investigation' },
    { id: 2, label: 'Block IP Address', icon: Shield, color: 'red', desc: 'Add to firewall blocklist' },
    { id: 3, label: 'Isolate System', icon: Server, color: 'orange', desc: 'Disconnect from network' },
    { id: 4, label: 'Generate Report', icon: BarChart3, color: 'cyan', desc: 'Create incident report' },
    { id: 5, label: 'Notify SOC Team', icon: Send, color: 'purple', desc: 'Alert security team' },
    { id: 6, label: 'Execute Playbook', icon: Zap, color: 'yellow', desc: 'Run automated response' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Setup Wizard Modal */}
        {showSetupWizard && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 rounded-2xl">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-blue-500/30 max-w-2xl w-full mx-4 shadow-2xl">
              <div className="border-b border-blue-500/20 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Cyber Strike Setup Wizard</h2>
                  <p className="text-slate-400 text-sm mt-1">Configure your SOC platform for production</p>
                </div>
                <button onClick={() => setShowSetupWizard(false)} className="text-slate-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  <div className="p-4 bg-slate-700/30 rounded-lg border border-blue-500/20 hover:border-blue-500/50 transition-all cursor-pointer">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-white">Step 1: Connect Data Sources</h4>
                        <p className="text-slate-400 text-sm mt-1">Configure firewall, EDR, cloud, and log ingestion</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-700/30 rounded-lg border border-blue-500/20 hover:border-blue-500/50 transition-all cursor-pointer">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-white">Step 2: Configure AI SOC Analyst</h4>
                        <p className="text-slate-400 text-sm mt-1">Set up machine learning models and threat detection</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-700/30 rounded-lg border border-blue-500/20 hover:border-blue-500/50 transition-all cursor-pointer">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-white">Step 3: Set Alert Thresholds</h4>
                        <p className="text-slate-400 text-sm mt-1">Define detection rules and notification triggers</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-700/30 rounded-lg border border-blue-500/20 hover:border-blue-500/50 transition-all cursor-pointer">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-white">Step 4: Configure Response Playbooks</h4>
                        <p className="text-slate-400 text-sm mt-1">Set up automated incident response workflows</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-blue-500/20 p-6 flex space-x-3">
                <button
                  onClick={() => setShowSetupWizard(false)}
                  className="flex-1 px-4 py-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-white font-bold transition-all"
                >
                  Close
                </button>
                <button
                  onClick={() => setShowSetupWizard(false)}
                  className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold transition-all"
                >
                  Start Setup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header with Controls */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 p-8 text-white shadow-2xl border border-cyan-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Shield className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">CYBER STRIKE</h1>
                  <span className="bg-yellow-300/20 text-yellow-300 px-3 py-1 rounded-lg text-sm font-bold border border-yellow-400/50">PRODUCTION-READY</span>
                </div>
                <p className="text-blue-100 text-lg font-semibold">AI-Powered Security Operations Center</p>
                <p className="text-blue-200 text-sm mt-1">Enterprise-Grade Threat Detection, Investigation & Automated Response</p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-black text-yellow-300 mb-2">{criticalCount}</div>
                <div className="text-sm text-blue-100 font-semibold">Active Threats</div>
                <div className="text-xs text-blue-200 mt-2">Requiring Action</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={() => setShowSetupWizard(true)}
                className="bg-yellow-400/20 hover:bg-yellow-400/40 text-yellow-300 px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 border border-yellow-400/50"
              >
                <Cpu className="w-4 h-4" />
                <span>Setup Wizard</span>
              </button>
              <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 border border-white/30">
                <RefreshCw className="w-4 h-4" />
                <span>Refresh Data</span>
              </button>
              <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 border border-white/30">
                <Upload className="w-4 h-4" />
                <span>Export Report</span>
              </button>
              <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 border border-white/30">
                <Settings className="w-4 h-4" />
                <span>Configuration</span>
              </button>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20 group cursor-pointer">
                <div className="flex items-start justify-between mb-4">
                  <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-lg shadow-lg group-hover:shadow-lg group-hover:scale-110 transition-all`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-bold text-green-400">{stat.trend}</span>
                </div>
                <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                <p className="text-white text-3xl font-black mt-2">{stat.value}</p>
              </div>
            )
          })}
        </div>

        {/* Main Content Tabs */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 shadow-lg overflow-hidden">
          <div className="flex items-center justify-between border-b border-blue-500/20 p-6 overflow-x-auto">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === 'dashboard'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span>Dashboard</span>
              </button>
              <button
                onClick={() => setActiveTab('threats')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === 'threats'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <AlertTriangle className="w-5 h-5" />
                <span>Threats</span>
              </button>
              <button
                onClick={() => setActiveTab('investigations')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === 'investigations'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Eye className="w-5 h-5" />
                <span>Investigations</span>
              </button>
              <button
                onClick={() => setActiveTab('datasources')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === 'datasources'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Database className="w-5 h-5" />
                <span>Data Sources</span>
              </button>
              <button
                onClick={() => setActiveTab('response')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === 'response'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Zap className="w-5 h-5" />
                <span>Response</span>
              </button>
            </div>
            <button
              onClick={() => setAdvancedMode(!advancedMode)}
              className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 whitespace-nowrap ${
                advancedMode
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Settings className="w-5 h-5" />
              <span>Advanced</span>
            </button>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* System Status */}
                <div className="bg-slate-700/30 rounded-lg border border-green-500/20 p-6">
                  <h3 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
                    <Cpu className="w-5 h-5 text-green-400" />
                    <span>System Status</span>
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-600/30 rounded-lg">
                      <span className="text-slate-300">Ingestion Rate</span>
                      <span className="text-green-400 font-bold">847K events/day</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-600/30 rounded-lg">
                      <span className="text-slate-300">AI Analyst Status</span>
                      <span className="text-green-400 font-bold flex items-center space-x-2"><Power size={16} />Active</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-600/30 rounded-lg">
                      <span className="text-slate-300">Detection Rate</span>
                      <span className="text-cyan-400 font-bold">99.8% Accuracy</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-600/30 rounded-lg">
                      <span className="text-slate-300">Response Time</span>
                      <span className="text-yellow-400 font-bold">1.2 seconds avg</span>
                    </div>
                  </div>
                </div>

                {/* Security Score */}
                <div className="bg-slate-700/30 rounded-lg border border-purple-500/20 p-6">
                  <h3 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
                    <Gauge className="w-5 h-5 text-purple-400" />
                    <span>Security Score</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-300">Overall</span>
                        <span className="text-blue-400 font-bold text-xl">78/100</span>
                      </div>
                      <div className="w-full bg-slate-600 rounded-full h-3">
                        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full" style={{width: '78%'}}></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="bg-slate-600/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Detection</p>
                        <p className="text-green-400 text-xl font-bold">98%</p>
                      </div>
                      <div className="bg-slate-600/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Prevention</p>
                        <p className="text-cyan-400 text-xl font-bold">92%</p>
                      </div>
                      <div className="bg-slate-600/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Response</p>
                        <p className="text-yellow-400 text-xl font-bold">87%</p>
                      </div>
                      <div className="bg-slate-600/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Compliance</p>
                        <p className="text-purple-400 text-xl font-bold">94%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI SOC Analyst */}
              <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 rounded-lg border border-purple-500/30 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-gradient-to-br from-purple-500 to-blue-500 p-3 rounded-lg">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">🤖 AI SOC Analyst - Real-Time Monitoring</h3>
                    <p className="text-slate-400 text-sm">Advanced machine learning engine with 24/7 autonomous threat detection</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-4">Continuously analyzing security logs, correlating events across your infrastructure, detecting anomalies, and providing actionable intelligence with sub-second response times.</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-xs">Events Analyzed</p>
                    <p className="text-white text-lg font-bold">847K+</p>
                    <p className="text-slate-500 text-xs">Last 24h</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-xs">Threats Found</p>
                    <p className="text-red-400 text-lg font-bold">142</p>
                    <p className="text-red-500 text-xs">Blocked</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-xs">Avg Response</p>
                    <p className="text-cyan-400 text-lg font-bold">1.2s</p>
                    <p className="text-slate-500 text-xs">Auto-respond</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-xs">Detection Rate</p>
                    <p className="text-green-400 text-lg font-bold">99.8%</p>
                    <p className="text-green-500 text-xs">Accuracy</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-slate-400 text-xs">False Positives</p>
                    <p className="text-yellow-400 text-lg font-bold">0.2%</p>
                    <p className="text-yellow-500 text-xs">Minimal</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Threats Tab */}
          {activeTab === 'threats' && (
            <div className="p-6">
              <div className="mb-6 flex flex-wrap gap-3">
                <div className="flex-1 min-w-64 relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search threats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500/50 font-semibold"
                >
                  <option value="all">All Status</option>
                  <option value="New">New</option>
                  <option value="In Review">In Review</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                </select>
                <select
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value)}
                  className="bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500/50 font-semibold"
                >
                  <option value="all">All Severity</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                </select>
              </div>

              <div className="space-y-3">
                {filteredFindings.map((finding) => (
                  <div key={finding.id} className="bg-slate-700/30 rounded-lg border border-slate-600/50 p-4 hover:border-blue-500/50 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(finding.severity)}`}>{finding.severity}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(finding.status)}`}>{finding.status}</span>
                          <span className="text-slate-400 text-xs">{finding.id}</span>
                        </div>
                        <h4 className="text-white font-bold mb-1">{finding.title}</h4>
                        <p className="text-slate-400 text-sm">Source: {finding.logSource} | Events: {finding.eventCount}</p>
                      </div>
                      <span className="text-slate-500 text-xs whitespace-nowrap ml-4">{finding.timestamp}</span>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/40 text-blue-300 rounded text-xs font-bold transition-all">
                        Investigate
                      </button>
                      <button className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-300 rounded text-xs font-bold transition-all">
                        Respond
                      </button>
                      <button className="px-3 py-1.5 bg-slate-600/30 hover:bg-slate-600/50 text-slate-300 rounded text-xs font-bold transition-all">
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Investigations Tab */}
          {activeTab === 'investigations' && (
            <div className="p-6">
              <button className="mb-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center space-x-2 transition-all">
                <Plus size={18} />
                <span>New Investigation</span>
              </button>
              <div className="space-y-3">
                {investigations.map((inv) => (
                  <div key={inv.id} className="bg-slate-700/30 rounded-lg border border-slate-600/50 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(inv.severity)}`}>{inv.severity}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(inv.status)}`}>{inv.status}</span>
                          <span className="text-slate-400 text-xs">{inv.id}</span>
                        </div>
                        <h4 className="text-white font-bold">{inv.title}</h4>
                        <p className="text-slate-400 text-sm mt-1">Findings: {inv.findings} | Detections: {inv.detections} | Affected Systems: {inv.affectedSystems}</p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex justify-between mb-1">
                        <span className="text-slate-400 text-xs">Progress</span>
                        <span className="text-slate-300 font-bold text-xs">{inv.progress}%</span>
                      </div>
                      <div className="w-full bg-slate-600 rounded-full h-2">
                        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full" style={{width: `${inv.progress}%`}}></div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/40 text-blue-300 rounded text-xs font-bold transition-all">
                        View Details
                      </button>
                      <button className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/40 text-purple-300 rounded text-xs font-bold transition-all">
                        Update Status
                      </button>
                      <button className="px-3 py-1.5 bg-slate-600/30 hover:bg-slate-600/50 text-slate-300 rounded text-xs font-bold transition-all">
                        Close
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data Sources Tab */}
          {activeTab === 'datasources' && (
            <div className="p-6">
              <button className="mb-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center space-x-2 transition-all">
                <Plus size={18} />
                <span>Add Data Source</span>
              </button>
              <div className="grid grid-cols-1 gap-3">
                {dataSources.map((ds) => (
                  <div key={ds.id} className="bg-slate-700/30 rounded-lg border border-slate-600/50 p-4 hover:border-blue-500/50 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="text-2xl">{ds.icon}</div>
                        <div className="flex-1">
                          <h4 className="text-white font-bold">{ds.name}</h4>
                          <p className="text-slate-400 text-sm">{ds.type} • {ds.events} • Last update: {ds.lastUpdate}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-lg text-xs font-bold ${getStatusColor(ds.status)}`}>{ds.status}</span>
                        <button className="p-2 hover:bg-slate-600/50 rounded-lg transition-all">
                          <Edit2 size={16} className="text-slate-400 hover:text-blue-400" />
                        </button>
                        <button className="p-2 hover:bg-slate-600/50 rounded-lg transition-all">
                          <Trash2 size={16} className="text-slate-400 hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Response Tab */}
          {activeTab === 'response' && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {responseActions.map((action) => {
                  const Icon = action.icon
                  return (
                    <button
                      key={action.id}
                      onClick={() => setSelectedResponse(action)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedResponse?.id === action.id
                          ? 'border-blue-500/50 bg-blue-500/20'
                          : 'border-slate-600/50 bg-slate-700/30 hover:border-blue-500/30'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <Icon className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-bold text-white">{action.label}</h4>
                          <p className="text-slate-400 text-sm mt-1">{action.desc}</p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>

              {selectedResponse && (
                <div className="bg-slate-700/30 rounded-lg border border-blue-500/30 p-6">
                  <h3 className="text-white font-bold text-lg mb-4">Configure: {selectedResponse.label}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-slate-300 font-bold text-sm">Threat IDs to Target</label>
                      <input type="text" placeholder="FIN-2025-001, FIN-2025-002..." className="w-full mt-2 bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50" />
                    </div>
                    <div>
                      <label className="text-slate-300 font-bold text-sm">Severity Threshold</label>
                      <select className="w-full mt-2 bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500/50">
                        <option>Critical Only</option>
                        <option>High & Critical</option>
                        <option>All Levels</option>
                      </select>
                    </div>
                    <div className="flex space-x-3 pt-4">
                      <button className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-all">
                        Execute Action
                      </button>
                      <button
                        onClick={() => setSelectedResponse(null)}
                        className="px-4 py-2 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg font-bold transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
